<template>
	<DataTable
		class="fixed-scroll-datatable"
		:value="data"
		:paginator="true"
		:rows="rows"
		:rowsPerPageOptions="[10, 20, 30]"
		responsiveLayout="scroll"
		:scrollable="true"
		scrollHeight="600px"
		scrollDirection="both"
		:sortable="true"
		:lazy="true"
		:totalRecords="totalRecords"
		:loading="loading"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
		@page="onPage"
		@sort="onSort"
	>
		<template #header>
			<div class="flex flex-wrap align-items-center gap-3">
				<AutoComplete
					v-model="selectedPoNumber"
					:suggestions="filteredPoNumbers"
					@complete="searchPoNumber($event)"
					:dropdown="true"
					field="po_number"
					placeholder="Filter PO Number"
					forceSelection
					class="w-full mb-2 lg:w-auto lg:mr-3 lg:mb-0"
				>
					<template #item="slotProps">
						{{ slotProps.item.po_number }}
					</template>
				</AutoComplete>
				<AutoComplete
					v-model="selectedVendor"
					:suggestions="filteredVendors"
					@complete="searchVendor($event)"
					:dropdown="true"
					field="vendor_name"
					placeholder="Filter Vendor Name"
					forceSelection
					class="w-full mb-2 lg:w-auto lg:mr-3 lg:mb-0"
				>
					<template #item="slotProps">
						{{ slotProps.item.vendor_name }}
					</template>
				</AutoComplete>
				<span class="p-input-icon-left w-full lg:w-auto">
					<i class="pi pi-search" />
					<InputText
						v-model="search"
						placeholder="Search"
						@input="onSearch"
					/>
				</span>
			</div>
		</template>
		<template #empty>No data found</template>
		<Column header="PO Number" field="poNumber" :sortable="true" style="width: 18%" />
		<Column header="Revision" field="revisionNum" :sortable="true" style="width: 10%" />
		<Column header="Vendor Name" field="vendorName" :sortable="true" style="width: 22%" />
		<Column header="Status Purchase Order" field="statusPurchaseOrder" :sortable="true" style="width: 25%" />
		<Column header="Status Send Email" field="statusSendEmail" :sortable="true" style="width: 25%" />
	</DataTable>
</template>

<script setup>
import { ref, watch, defineProps } from 'vue';
import { getReportPoStatus, getReportPoStatusFilters } from '@/service/ReportPoStatus';

const props = defineProps({
	company: { type: Object, default: null }
});

const data = ref([]);
const totalRecords = ref(0);
const loading = ref(false);
const search = ref('');
const rows = ref(10);
const page = ref(0);
const sortBy = ref('');
const sortType = ref('ASC');
const poNumbers = ref([]);
const filteredPoNumbers = ref([]);
const selectedPoNumber = ref(null);
const vendors = ref([]);
const filteredVendors = ref([]);
const selectedVendor = ref(null);

let searchTimer = null;

const searchPoNumber = (event) => {
	setTimeout(() => {
		if (!event.query.trim().length) {
			filteredPoNumbers.value = [...poNumbers.value];
		} else {
			filteredPoNumbers.value = poNumbers.value.filter((p) =>
				p.po_number.toLowerCase().startsWith(event.query.toLowerCase())
			);
		}
	}, 250);
};

const searchVendor = (event) => {
	setTimeout(() => {
		if (!event.query.trim().length) {
			filteredVendors.value = [...vendors.value];
		} else {
			filteredVendors.value = vendors.value.filter((v) =>
				v.vendor_name.toLowerCase().includes(event.query.toLowerCase())
			);
		}
	}, 250);
};

const loadFilters = async () => {
	if (!props.company) return;
	try {
		const result = await getReportPoStatusFilters(
			props.company.value,
			props.company.db_instance
		);
		if (result) {
			poNumbers.value = result.poNumbers || [];
			vendors.value = result.vendors || [];
		}
	} catch (e) {
		console.error(e);
	}
};

const loadData = async () => {
	loading.value = true;
	try {
		const result = await getReportPoStatus(
			page.value + 1,
			rows.value,
			search.value,
			props.company ? props.company.value : null,
			props.company ? props.company.db_instance : null,
			selectedPoNumber.value ? selectedPoNumber.value.po_number : null,
			selectedVendor.value ? selectedVendor.value.vendor_id : null,
			sortBy.value || undefined,
			sortType.value
		);
		if (result) {
			data.value = result.data;
			totalRecords.value = result.totalRecords;
		}
	} catch (e) {
		console.error(e);
		data.value = [];
		totalRecords.value = 0;
	} finally {
		loading.value = false;
	}
};

const onPage = (event) => {
	page.value = event.page;
	rows.value = event.rows;
	loadData();
};

const onSort = (event) => {
	sortBy.value = event.sortField;
	sortType.value = event.sortOrder === 1 ? 'ASC' : 'DESC';
	loadData();
};

const onSearch = () => {
	if (searchTimer) clearTimeout(searchTimer);
	searchTimer = setTimeout(() => {
		page.value = 0;
		loadData();
	}, 500);
};

watch(() => props.company, () => {
	page.value = 0;
	selectedPoNumber.value = null;
	selectedVendor.value = null;
	loadFilters();
	loadData();
}, { deep: true });

watch(selectedPoNumber, () => {
	page.value = 0;
	loadData();
});

watch(selectedVendor, () => {
	page.value = 0;
	loadData();
});

loadFilters();
loadData();
</script>

<style scoped></style>
